<!-- 
 >> Schema 
options:[{
   "text":<string>
   "value":<string>
}] 
dynamicOptions:<string>
-->
<template>
   <div
      class="multi-select-field"
      ref="el"
      :class="[
         inputStyle,
         { 'has-error': !!errorMessage, success: meta.valid },
      ]"
      v-click-away="closeDD"
   >
      <div
         class="fake-select"
         ref="fieldEl"
         :class="{ active: ddActive }"
         @click="toggleDD"
      >
         <template v-if="fieldValue?.length" v-for="(tag, i) in readableValues">
            <Tag class="multi-select" v-if="i < tagsToShow">{{ tag }}</Tag>
         </template>
         <Tag class="basic" v-if="extraTags > 0">+{{ extraTags }}</Tag>
         <p v-if="!fieldValue?.length">{{ placeholder }}</p>
      </div>
      <transition @enter="handleEnter" @leave="handleLeave">
         <div
            class="dropdown"
            v-if="ddActive"
            data-lenis-prevent
            ref="dropdown"
         >
            <input
               v-if="searchable"
               v-model="searchTerm"
               name="searchTerm"
               type="text"
               class="small"
               placeholder="Search"
            />
            <div
               v-for="(
                  { tag, text, value, ...childAttrs }, idx
               ) in filteredOptions"
               class="option"
               :class="{ selected: fieldValue.includes(value) }"
               @click="toggleValue(value)"
               :key="idx"
            >
               {{ text }}<IconCheck v-if="fieldValue.includes(value)" />
            </div>
         </div>
      </transition>
   </div>
</template>

<script setup lang="ts">
import { clickAway as vClickAway } from "~/directives/clickAway.js";
import IconCheck from "~/assets/images/icons/check.svg";
import { useField } from "vee-validate";
const gsap = useNuxtApp().$gsap;
const tagsToShow = ref(2);
const emit = defineEmits(["change"]);
const ddActive = ref(false);
const ddOptions = ref([]);
const props = defineProps({
   value: {
      type: [String, Array, Number],
      default: undefined,
   },
   name: {
      type: String,
      required: true,
   },
   placeholder: {
      type: String,
      default: "",
   },
   searchable: {
      type: Boolean,
      default: false,
   },
   inputStyle: {
      type: String,
      default: "normal",
      validator: (value) => ["simple", "normal"].includes(value),
   },
   options: {
      type: Array,
      required: false,
      default: null,
   },
   dynamicOptions: {
      type: String,
      required: false,
      default: null,
   },
});
const name = toRef(props, "name");
const searchTerm = ref("");
// Rules are set at form level
const {
   value: fieldValue,
   errorMessage,
   setTouched,
   setValue,
   meta,
} = useField(name, undefined, {
   initialValue: props.value ?? [],
});

// >> Methods
const toggleDD = () => {
   ddActive.value = !ddActive.value;
};
const closeDD = () => {
   ddActive.value = false;
};
const toggleValue = (val) => {
   setTouched(true);
   if (val == "") {
      fieldValue.value = [];
      return;
   }
   if (fieldValue.value.includes(val)) {
      const newVal = fieldValue.value.filter((item) => item != val);
      setValue(newVal, true);
   } else {
      const newVal = [...fieldValue.value, val];
      setValue(newVal, true);
   }
   emit("change");
};

// >> WATCHERS

// >> COMPUTED

const readableValues = computed(() => {
   if (fieldValue.value?.length == 0 && ddOptions.value?.length) return [];

   return fieldValue.value?.map((item) => {
      const match = ddOptions.value?.filter((op) => op.value == item);
      return match[0]?.text ?? "";
   });
});

const extraTags = computed(
   () => readableValues.value?.length - tagsToShow.value
);

const dropdown = ref(null);
const filteredOptions = computed(() => {
   // FUSE
   const Fuse = useNuxtApp().$fuse;
   const fuseSearch = new Fuse(ddOptions.value, {
      keys: ["text"],
   });
   if (searchTerm.value.length > 0) {
      return fuseSearch.search(searchTerm.value).map((result) => result.item);
   } else {
      return ddOptions.value;
   }
});

//>> Animations
const handleEnter = (el, done) => {
   gsap.to(el, {
      height: "auto",
      onComplete: done,
      duration: 0.5,
      ease: "cubic.inOut",
   });
};
const handleLeave = (el, done) => {
   gsap.to(el, {
      height: 0,
      onComplete: () => {
         searchTerm.value = "";
         done();
      },
      duration: 0.5,
      ease: "cubic.inOut",
   });
};

onBeforeMount(async () => {
   if (props.dynamicOptions) {
      ddOptions.value = await useMultiSelectData(props.dynamicOptions);
   } else {
      ddOptions.value = props.options;
   }
});
</script>
<style scoped lang="scss">
@import "~/assets/scss/partials/select-fields";
</style>
